<template>
  <div class="ledger-page">
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner variant="primary" />
    </div>
    <b-card
      v-else-if="quote"
      class="rounded-lg"
    >
      <div class="d-flex justify-content-between align-items-start flex-wrap">
        <div>
          <h3 class="mb-50">
            {{ quote.document_number }}
          </h3>
          <div>{{ quote.type }}</div>
          <div class="text-muted small">
            {{ quote.quote_date }}
            <span v-if="quote.payment_mode"> · {{ quote.payment_mode }}</span>
          </div>
          <div
            v-if="quote.gl_account"
            class="mt-50"
          >
            {{ accountLabel(quote.gl_account) }}
          </div>
          <b-badge
            class="mt-50"
            :variant="quote.status === 'posted' ? 'success' : 'secondary'"
          >
            {{ quote.status === 'posted' ? $t('ledger_quote_posted') : $t('ledger_quote_draft') }}
          </b-badge>
          <b-badge
            v-if="quote.is_locked"
            variant="dark"
            class="mt-50 ml-50"
          >
            {{ $t('ledger_quote_locked') }}
          </b-badge>
        </div>
        <b-button
          v-if="canPost"
          variant="primary"
          class="rounded-pill"
          :disabled="posting"
          @click="post"
        >
          {{ posting ? $t('ledger_saving') : $t('ledger_post') }}
        </b-button>
      </div>

      <p
        v-if="quote.notes"
        class="mt-1 mb-0"
      >
        {{ quote.notes }}
      </p>

      <b-table
        class="mt-1 ledger-dense-table"
        :items="quote.lines || []"
        :fields="fields"
        small
        responsive
      >
        <template #cell(quantity)="data">
          <span class="ledger-num">{{ data.item.quantity }}</span>
        </template>
        <template #cell(unit_price)="data">
          <span class="ledger-num">{{ money(data.item.unit_price) }}</span>
        </template>
        <template #cell(line_total)="data">
          <span class="ledger-num">{{ money(data.item.line_total) }}</span>
        </template>
      </b-table>

      <div class="ledger-num">
        <div>{{ $t('ledger_quote_subtotal') }} {{ money(quote.subtotal) }}</div>
        <div>{{ $t('ledger_quote_tax') }} {{ money(quote.tax_amount) }}</div>
        <strong>{{ $t('ledger_inv_total') }} {{ money(quote.total) }}</strong>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BButton, BBadge, BTable, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getQuote, postQuote } from '@/api/accounting/quotes'
import {
  oneFrom, formatLedgerMoney, accountOptionLabel, apiErrorMessage,
} from '@/utils/ledger'

export default {
  name: 'QuoteShow',
  components: {
    BCard, BButton, BBadge, BTable, BSpinner,
  },
  data() {
    return {
      loading: false,
      posting: false,
      quote: null,
    }
  },
  computed: {
    canPost() {
      if (!this.quote || !this.hasPermission('send invoices')) return false
      return this.quote.status === 'draft' && !this.quote.is_locked
    },
    fields() {
      return [
        { key: 'description', label: this.$t('ledger_description') },
        { key: 'quantity', label: this.$t('ledger_maps_qty') },
        { key: 'unit_price', label: this.$t('ledger_maps_unit_price') },
        { key: 'line_total', label: this.$t('ledger_inv_total') },
      ]
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    accountLabel(account) {
      return accountOptionLabel(account, this.$i18n.locale)
    },
    async load() {
      this.loading = true
      try {
        const res = await getQuote(this.$route.params.id)
        this.quote = oneFrom(res)
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    async post() {
      this.posting = true
      try {
        const res = await postQuote(this.quote.id)
        this.quote = oneFrom(res)
        this.toast(this.$t('ledger_quote_posted_ok'), 'success')
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.posting = false
      }
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; }
</style>
